import React, { useEffect, useMemo } from 'react';
import { LorawanActionTypes } from '@utils/deviceActions';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import { useTranslation } from 'react-i18next';
import { Form, FormInstance } from 'antd';
import TagsManagement from '@components/TagsManagement';
import rolesConstants from '@utils/rolesConstants';

interface ChangeTagsPayloadProps {
  action: LorawanActionTypes.CHANGE_TAGS;
  devices: {
    devEui: string;
    tags: string[];
  }[];
}

interface ChangeTagsProps {
  devices: {
    devEui: string;
    tags: string[] | string;
  }[];
  isUpload?: boolean;
  form: FormInstance<any>;
}

const useChangeTags = ({
  devices,
  isUpload,
  form,
}: ChangeTagsProps): DeviceActionProps => {
  const { t } = useTranslation(['lorawan', 'device-actions']);
  const tags = Form.useWatch('tags', form);

  useEffect(() => {
    if (devices?.length === 1 && devices?.[0]?.tags) {
      form.setFieldValue('tags', devices[0].tags);
    }
  }, [devices, form]);

  const steps: DeviceActionStepsProps[] = useMemo(
    () => [
      {
        title: t('newTags'),
        name: 'tags',
        content: (
          <TagsManagement
            style={{ padding: '0 4px' }}
            tags={tags}
            onChange={newTags => {
              form.setFieldValue('tags', newTags);
            }}
          />
        ),
      },
    ],
    [form, t, tags],
  );

  const devicesUploaded = useMemo(
    () =>
      isUpload
        ? devices?.map(device => ({
            ...device,
            tags: !Array.isArray(device?.tags) ? device?.tags?.split(';') : [],
          }))
        : [],
    [devices, isUpload],
  );

  const payloadToSend = useMemo(
    (): ChangeTagsPayloadProps => ({
      action: LorawanActionTypes.CHANGE_TAGS,
      devices: isUpload
        ? devicesUploaded
        : devices?.map(device => ({
            devEui: device.devEui,
            tags,
          })),
    }),
    [isUpload, devicesUploaded, devices, tags],
  );

  return {
    steps,
    payloadToSend,
    title: t(
      `device-actions:labels.lorawan.${rolesConstants.LORAWAN_CHANGE_TAGS}`,
    ),
  };
};

export default useChangeTags;
